/**
 * Passkey orchestration: challenge → ceremony → submit (CU-86ajzr8r0).
 *
 * Joins the two independently-testable halves (PasskeyProvider runs the browser
 * ceremony; passkeyClient talks to the Cloud Function) into the flows the UI
 * actually needs. No React, no routing, no Firebase session handling — the
 * caller owns those, so these stay unit-testable with simple mocks.
 *
 * Outcomes are deliberately coarse and non-throwing: every screen that will
 * consume this has a guaranteed password path, so a failed ceremony is a fork in
 * the UI, not an exception to handle.
 *
 * PORTED FROM USACS-MIDEO-Admin-Web-v2's `passkeyFlows.ts`, itself ported from
 * mideo-health-app. THE OUTCOME UNIONS BELOW ARE CARRIED ACROSS VERBATIM IN
 * SUBSTANCE, INCLUDING THEIR COMMENTS — those comments document three separate
 * real incidents caused by collapsing error cases, and the structure is retained
 * even where a member looks redundant on the web. See each ⚠️ block.
 *
 * ⚠️ THIS FILE SHIPS CONSUMED BY NOTHING, AND THAT IS THE POINT. Sub-task 1
 * delivers the seam as an independently testable unit; sub-task 2 (CU-86ajzr8x1,
 * enrollment) is its first consumer, and sub-task 3 (CU-86ajzr94n) wires login.
 *
 * ⚠️ CONSUMERS CALL THIS FILE AND ONLY THIS FILE. Nothing in `hooks/`,
 * `components/` or `routes/` may reach past this layer to `passkeyClient` or
 * `webPasskeyProvider` — the one-file-imports-the-library invariant (see
 * PasskeyProvider.ts) depends on the layering, not on anyone remembering it.
 */

import * as Sentry from '@sentry/react';
import type { PasskeyCredentialSummary } from '@usacsmideo/types';
import type { PasskeyProvider } from './PasskeyProvider';
import { webPasskeyProvider } from './webPasskeyProvider';
import { requestChallenge, submitRegistration, submitAssertion, listPasskeys, revokePasskey } from './passkeyClient';
import { isNetworkError } from './networkError';

/** Enrollment result. `cancelled` is benign — the patient dismissed the sheet. */
export type EnrollOutcome =
  | { status: 'enrolled' }
  | { status: 'cancelled' }
  | { status: 'unsupported' }
  | { status: 'already-registered' }
  /**
   * The ceremony could not reach the server (CU-86ajwh66w, checklist D2).
   *
   * ⚠️ A DISTINCT STATUS BECAUSE THE USER'S NEXT STEP IS DIFFERENT. `failed`
   * means "something broke, try again or use the other tier"; `network` means
   * "reconnect and this will work." Collapsing them is what left an offline
   * patient at the mobile MFA gate reading "Setup could not be completed" — a
   * support call for a condition they could have fixed themselves.
   *
   * ⚠️ ONLY set from the OUTER CATCH, never from a ceremony failure. The
   * browser ceremony runs locally: a `cancelled` / `not-supported` outcome says
   * nothing about connectivity, and the legs that DO touch the network
   * (requestChallenge, submitRegistration) throw into the catch.
   *
   * ⚠️ This was the THIRD collapsed error return in the mobile barrel to cost
   * real debugging — after `upgradeToPasskeySession`'s boolean (which threw away
   * cancel-vs-no-credential and produced a dead end at the gate, CU-86ajw3hv9
   * PRs #308/#310) and `useSmsEnrollment`'s missing offline kind. Prefer a
   * distinguishable return over copy that papers over an indistinguishable one.
   */
  | { status: 'network' }
  | { status: 'failed' };

/**
 * Login result. `no-credentials` means nothing is enrolled for this domain —
 * the ordinary first-run case, not an error.
 */
export type LoginOutcome =
  | { status: 'verified'; customToken: string }
  | { status: 'cancelled' }
  | { status: 'unsupported' }
  /**
   * ⚠️ RETAINED FROM MOBILE THOUGH THE MODAL WEB CEREMONY DOES NOT PRODUCE IT.
   * The browser reports an empty credential store as `NotAllowedError` —
   * identical to a dismissal — so `webPasskeyProvider` maps that to `cancelled`
   * and this member is currently unreachable from a modal ceremony.
   *
   * It stays because sub-task 3 makes it reachable: conditional mediation
   * (autofill) is precisely the browser mechanism that reveals an empty store
   * WITHOUT a modal, which is the native solution to the problem the mobile
   * codebase needed ~465 lines of hint-store apparatus to approximate. Deleting
   * the member now means re-adding it in sub-task 3 and re-teaching every caller
   * a case it had been allowed to forget.
   */
  | { status: 'no-credentials' }
  /**
   * The ceremony could not reach the server (CU-86ajwh66w, checklist D2).
   *
   * ⚠️ THIS IS THE ONE THE MOBILE MFA GATE ACTUALLY HIT. Its Tier-0 button ran
   * `upgradeToPasskeySession` → `loginWithPasskey`, NOT `enrollPasskey` — so
   * fixing only the enrollment side would have left the exact button under test
   * unchanged. Same outer-catch-only rule as EnrollOutcome's `network`.
   */
  | { status: 'network' }
  | { status: 'failed'; detail?: string };

/**
 * Enroll a passkey for the signed-in patient.
 *
 * `reauthProof` MUST be a freshly-minted ID token from an EXPLICIT
 * reauthentication (M-E) — the caller obtains it by re-prompting for the
 * password; a merely time-fresh session is rejected server-side.
 *
 * ⚠️ Sub-task 2 owns the hard part of obtaining that proof: a patient who
 * already has SMS enrolled cannot complete `reauthenticateWithCredential` with a
 * password alone (IdP throws `auth/multi-factor-auth-required`). That is a
 * caller concern by design — this function takes the finished proof.
 */
export async function enrollPasskey(
  params: {
    userId: string;
    userName: string;
    userDisplayName: string;
    reauthProof: string;
    deviceLabel?: string;
  },
  provider: PasskeyProvider = webPasskeyProvider,
): Promise<EnrollOutcome> {
  if (!provider.isSupported()) return { status: 'unsupported' };

  try {
    // Leg 1 — server-issued challenge, for the patient-portal RP.
    // `excludeCredentials` comes back populated (and server-filtered to THIS
    // RP) so the platform won't mint a duplicate (BIND-UNIQ).
    const challenge = await requestChallenge('register');

    // Leg 2 — the browser ceremony.
    const ceremony = await provider.register({
      challenge: challenge.challenge,
      rpId: challenge.rpId,
      userId: params.userId,
      userName: params.userName,
      userDisplayName: params.userDisplayName,
      timeoutMs: challenge.timeoutMs,
      excludeCredentials: challenge.excludeCredentials,
    });
    if (!ceremony.ok) {
      if (ceremony.reason === 'cancelled') return { status: 'cancelled' };
      if (ceremony.reason === 'not-supported') return { status: 'unsupported' };
      // `InvalidStateError` on create — this authenticator already holds a
      // credential for this RP. Actionable and expected, not a fault, so it
      // does NOT get a Sentry capture.
      if (ceremony.reason === 'already-registered') {
        return { status: 'already-registered' };
      }
      // A real ceremony failure. The mobile equivalent of this branch
      // previously only breadcrumbed — which creates NO Sentry issue — so an
      // in-the-field failure here was invisible. Capture it with the library's
      // error detail so the actual reason surfaces.
      Sentry.captureMessage(`enroll ceremony failed: ${ceremony.reason}`, {
        level: 'error',
        tags: { feature: 'passkey', step: 'enroll-ceremony' },
        extra: { reason: ceremony.reason, detail: ceremony.detail ?? 'n/a' },
      });
      return { status: 'failed' };
    }

    // Leg 3 — server verifies the attestation and stores the binding.
    await submitRegistration({
      attestation: ceremony.attestation,
      reauthProof: params.reauthProof,
      ...(params.deviceLabel ? { deviceLabel: params.deviceLabel } : {}),
    });
    return { status: 'enrolled' };
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    // ⚠️ The message MUST be passed to isNetworkError, not just the code: a
    // codeless transport failure is invisible to a code-only predicate. Measured
    // on mobile 2026-08-06; the browser fetch failures this repo will hit are
    // likewise often prose-only ("Failed to fetch").
    const message = (error as { message?: string })?.message ?? '';
    // `already-exists` is actionable and expected when re-enrolling a credential
    // the account already holds; everything else is generic. Checked FIRST — it
    // is a legitimate outcome, not a transport failure.
    //
    // NOTE this is the SERVER-side duplicate (BIND-UNIQ rejecting the write),
    // distinct from the ceremony-side `already-registered` above, which the
    // authenticator raises without ever reaching the server. Both land on the
    // same status because the user's next step is identical.
    if (code === 'functions/already-exists') {
      return { status: 'already-registered' };
    }
    // ── CU-86ajwh66w — offline is not a fault ──────────────────────────────
    // `requestChallenge` and `submitRegistration` are the network legs; both
    // throw here. Breadcrumb rather than capture: a dropped connection is
    // expected, and capturing would alert on every one.
    //
    // ⚠️ NARROW ON PURPOSE. Anything not recognised as transport stays `failed`
    // and KEEPS its captureException — that capture is our only evidence a real
    // enrollment fault exists, and widening this predicate would silence it.
    if (isNetworkError(code, message)) {
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'warning',
        message: 'enroll: could not reach the server',
        data: { code },
      });
      return { status: 'network' };
    }
    Sentry.captureException(error, {
      tags: { feature: 'passkey', step: 'enroll' },
    });
    return { status: 'failed' };
  }
}

/**
 * Log in from a LOGGED-OUT state with a passkey.
 *
 * Returns the custom token for the caller to exchange via
 * `signInWithCustomToken`. Every server-side failure collapses to the same
 * uniform error (H5), so this cannot and does not distinguish "no such
 * credential" from "signature rejected" — by design.
 *
 * ⚠️ SHIPS UNWIRED, DELIBERATELY — enrollment lands first (sub-task 2) so a
 * broken passkey login cannot lock anyone out while password+SMS is the only way
 * in. Sub-task 3 wires it, and BOTH login paths (the autofill request and the
 * visible button) must route through this one function.
 *
 * ⚠️ `signInWithCustomToken` IS ABSENT FROM THE PORTAL'S AuthContext and must be
 * added there against the NAMED Firebase app — a sub-task 3 concern, noted here
 * because this function's return value is unusable without it.
 *
 * `options.conditional` selects conditional mediation (autofill) over a modal
 * ceremony. Omitted ⇒ modal.
 *
 * ⚠️ THE PASSWORD PATH REMAINS PERMANENTLY VISIBLE AND UNGATED regardless of
 * anything this function returns (invariant #1, carried from the mobile design),
 * and SMS remains live alongside it — both factors stay through the prod
 * release. The argument for keeping SMS is STRONGER here than on the console:
 * patients are far more device-diverse than three admins.
 */
export async function loginWithPasskey(
  provider: PasskeyProvider = webPasskeyProvider,
  options: { conditional?: boolean } = {},
): Promise<LoginOutcome> {
  if (!provider.isSupported()) return { status: 'unsupported' };

  try {
    const challenge = await requestChallenge('authenticate');

    const ceremony = await provider.authenticate({
      challenge: challenge.challenge,
      rpId: challenge.rpId,
      timeoutMs: challenge.timeoutMs,
      // Sub-task 3. Defaults to a MODAL ceremony when omitted, so a caller that
      // does not opt in keeps the modal behaviour exactly.
      conditional: options.conditional === true,
    });
    if (!ceremony.ok) {
      if (ceremony.reason === 'cancelled') return { status: 'cancelled' };
      if (ceremony.reason === 'not-supported') return { status: 'unsupported' };
      if (ceremony.reason === 'no-credentials') {
        return { status: 'no-credentials' };
      }
      // Log the RAW library/browser detail, not just our coarse reason. On
      // mobile the analogous omission hid the fact that iOS reported an empty
      // credential store as RequestFailed rather than NoCredentials, and that
      // difference is exactly what left patients stuck. `detail` is what makes
      // the real error visible in Sentry.
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'warning',
        message: `login ceremony failed: ${ceremony.reason}`,
        data: { reason: ceremony.reason, detail: ceremony.detail ?? 'n/a' },
      });
      return { status: 'failed', detail: ceremony.detail };
    }

    const { customToken } = await submitAssertion(ceremony.assertion);
    return { status: 'verified', customToken };
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    const message = (error as { message?: string })?.message ?? '';
    // ── CU-86ajwh66w ────────────────────────────────────────────────────────
    // ⚠️ This is NOT branching on the server's error, which is uniform by
    // design (H5). A transport failure means the request never REACHED the
    // server, so there is no uniform error to respect — that invariant is about
    // not distinguishing "no such credential" from "signature rejected", both of
    // which require the server to have answered.
    if (isNetworkError(code, message)) {
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'warning',
        message: 'login: could not reach the server',
        data: { code },
      });
      return { status: 'network' };
    }
    // Do NOT branch on the server error: it is uniform by design (H5).
    Sentry.captureException(error, {
      tags: { feature: 'passkey', step: 'login' },
    });
    return { status: 'failed' };
  }
}

/**
 * Whether this browser can offer conditional mediation (autofill) — sub-task 3.
 *
 * ⚠️ EXPOSED HERE SO THE LOGIN HOOK NEVER REACHES PAST THIS LAYER. The seam
 * discipline is that `passkeyFlows` is the ONLY module the UI imports from
 * `services/passkey/`; the one-file-imports-the-library invariant is maintained
 * by that layering rather than by anyone remembering it.
 *
 * Answers "should we ARM the autofill request", never "may this patient log in".
 * A false answer degrades to the visible button and the password path, both of
 * which remain.
 *
 * ⚠️ ASYNC. A synchronous gate on this would always be truthy — a pending
 * Promise is truthy — and would arm a conditional request on every browser.
 */
export async function passkeyAutofillAvailable(provider: PasskeyProvider = webPasskeyProvider): Promise<boolean> {
  if (!provider.isSupported()) return false;
  return provider.supportsAutofill();
}

/**
 * Cancel any in-flight ceremony — sub-task 3.
 *
 * ⚠️ FOR TEARDOWN ONLY. Superseding is already handled by the library: starting
 * a ceremony aborts any previous one. Callers use this from an unmount cleanup,
 * so a conditional request armed on the login screen cannot outlive it.
 */
export function cancelPasskeyCeremony(provider: PasskeyProvider = webPasskeyProvider): void {
  provider.cancelCeremony();
}

// ─────────────────────────────────────────────────────────────────────────────
// CREDENTIAL MANAGEMENT (CU-86ajz1bhx server; sub-task 2 client)
//
// The per-device list and per-credential removal behind the enrollment UI. Same
// discipline as the flows above: coarse, NON-THROWING outcomes, because every
// screen consuming these has a working alternative path and a failed management
// call is a fork in the UI rather than an exception to handle.
// ─────────────────────────────────────────────────────────────────────────────

/** Result of listing this surface's credentials. */
export type ListOutcome =
  | { status: 'listed'; credentials: PasskeyCredentialSummary[] }
  /** Same distinct-transport-status reasoning as the flows above. */
  | { status: 'network' }
  | { status: 'failed' };

/** Result of removing ONE credential. */
export type RevokeOutcome =
  | { status: 'revoked' }
  /**
   * The credential is not on this account/surface — already removed on another
   * tab or device, or a stale list.
   *
   * ⚠️ THE SERVER CANNOT AND MUST NOT TELL US WHICH. Unknown credId, another
   * account's credId, and a wrong-surface credId all return the same uniform
   * failure so this cannot be used to probe what an account holds. So the copy
   * behind this status must not assert a reason — "we couldn't find that passkey,
   * refresh and try again" is the honest ceiling.
   */
  | { status: 'not-found' }
  | { status: 'network' }
  | { status: 'failed' };

/**
 * List the patient-portal credentials for the signed-in patient.
 *
 * Surface-scoped server-side, so this never returns the caller's mobile
 * passkeys. Ordering comes from the server (newest first by `createdAt`) and is
 * NOT re-sorted here — `deviceLabel` is client-supplied and must never become
 * the sort key.
 */
export async function listMyPasskeys(): Promise<ListOutcome> {
  try {
    const { credentials } = await listPasskeys();
    return { status: 'listed', credentials };
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    const message = (error as { message?: string })?.message ?? '';
    if (isNetworkError(code, message)) {
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'warning',
        message: 'list: could not reach the server',
        data: { code },
      });
      return { status: 'network' };
    }
    Sentry.captureException(error, {
      tags: { feature: 'passkey', step: 'list' },
    });
    return { status: 'failed' };
  }
}

/**
 * Remove ONE credential by id.
 *
 * ⚠️ TARGETED, AND THAT IS THE WHOLE POINT. This must never be swapped for an
 * account-wide revoke: the driving case is "revoke the stolen phone, NOT the
 * laptop in my hand", and a revoke-all also kills every live session. The
 * substitution is invisible from the UI — the button still succeeds — so it is
 * pinned by a named test rather than left to review. (This repo deliberately
 * wraps no revoke-all at all; see the passkeyClient.ts header.)
 *
 * ⚠️ NO PHI / NO SECRETS IN TELEMETRY. `credId` is never sent to Sentry; it is a
 * credential identifier tied to one account. The server logs a truncated prefix
 * for correlation, which is where that belongs.
 */
export async function revokeMyPasskey(credId: string): Promise<RevokeOutcome> {
  try {
    await revokePasskey(credId);
    return { status: 'revoked' };
  } catch (error) {
    const code = (error as { code?: string })?.code ?? '';
    const message = (error as { message?: string })?.message ?? '';
    // Actionable and expected: the list was stale, or another tab removed it.
    // Checked BEFORE the transport predicate — it is a legitimate outcome.
    if (code === 'functions/not-found') {
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'info',
        message: 'revoke: credential not found on this account/surface',
      });
      return { status: 'not-found' };
    }
    if (isNetworkError(code, message)) {
      Sentry.addBreadcrumb({
        category: 'passkey',
        level: 'warning',
        message: 'revoke: could not reach the server',
        data: { code },
      });
      return { status: 'network' };
    }
    Sentry.captureException(error, {
      tags: { feature: 'passkey', step: 'revoke' },
    });
    return { status: 'failed' };
  }
}
